import { useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Heart, Loader2, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import { arrayRemove, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import SettingsLayout from "./SettingsLayout";
import useSettingsProfile from "./useSettingsProfile";
import useUserProfileList from "./useUserProfileList";
import { db } from "../../firebase";
import ProfileCard from "../../Components/ProfileCard";
import EmptyState from "../../Components/EmptyState";
import ConfirmDialog from "../../Components/ConfirmDialog";

export default function ShortlistedProfilesSettings() {
  const { user } = useSettingsProfile();
  const { items, setItems, loading } = useUserProfileList(user, "shortlisted");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = items.filter((p) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return [p.fullName, p.profession, p.education, p.presentResidence, p.state]
      .filter(Boolean).join(" ").toLowerCase().includes(term);
  });

  const remove = async () => {
    if (!user || !pending) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "users", user.uid), {
        shortlisted: arrayRemove(pending.id),
        updatedAt: serverTimestamp(),
      });
      setItems((c) => c.filter((it) => it.id !== pending.id));
      toast.success("Removed from shortlist.");
      setPending(null);
    } catch (err) {
      toast.error(err.message || "Could not update shortlist.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsLayout
      title="Shortlisted"
      description="Profiles you've saved. Tap to view details or remove anytime."
    >
      {loading ? (
        <div className="card flex justify-center p-10">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          Icon={Bookmark}
          tone="amber"
          title="Your shortlist is empty"
          description="Tap Shortlist on any profile to save it here."
          action={<Link to="/matches" className="btn-primary px-5">Browse matches</Link>}
        />
      ) : (
        <>
          {/* Search */}
          <div className="mb-4 flex items-center gap-2">
            <div className="input-group flex-1 h-11">
              <Search size={16} className="text-ink-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, ID, profession..."
                className="text-[13px]"
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              Icon={Search}
              title="No matches"
              description="Try a different search term."
            />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((profile) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    variant="grid"
                    overlayActions
                    actions={
                      <button
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setPending(profile);
                        }}
                        aria-label="Remove from shortlist"
                        title="Remove from shortlist"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/80 bg-white/95 text-rose-600 shadow-soft transition hover:scale-105 hover:bg-white"
                      >
                        <Heart size={17} className="fill-current" />
                      </button>
                    }
                  />
                ))}
            </div>
          )}
        </>
      )}


      <ConfirmDialog
        open={!!pending}
        title="Remove from shortlist?"
        description={pending ? `${pending.fullName} will be removed from your saved list.` : ""}
        Icon={Bookmark}
        tone="warning"
        confirmLabel="Yes, remove"
        loading={busy}
        onClose={() => !busy && setPending(null)}
        onConfirm={remove}
      />
    </SettingsLayout>
  );
}
